<template>
  <image-header-page class="image-header-page padding-bottom-xl" :image-header-props="imageHeaderProps">
    <template v-slot:header>
      <div class="header-text flex-column full-height full-width align-right" >
        <div class="header-text ri-body margin-top-sm flex-row align-center">
          <img :src="require('@/assets/icons/mortgages-flower.svg')" class="margin-bottom-xxs margin-left-xxs"/>
          המשכנתא של {{householdName}}
        </div>
        <div class="ri-medium-bold-display margin-top-l">
          <span>מיחזור משכנתא יכול</span>
          <span class="yellow-text">לחסוך עשרות אלפי שקלים.</span>
          <span>רייזאפ לוקחת אותך לשם יד ביד.</span>
        </div>
      </div>
    </template>
    <template v-slot:overlayContent>
      <card v-if="!uploadingFile" class="cards-container padding-vertical-ml">
        <div class="flex-column">
          <div class="ri-body black-text center-text">
            צרפו את דו״ח סיכום היתרות של המשכנתא שלכם.
            <span class="ri-bold-body url-blue clickable" @click="openPopUp">איך מוצאים אותו?</span>
          </div>
          <input
            ref="fileInput"
            type="file"
            accept=".pdf"
            style="display: none;"
            @change="onFileSelected"
          />
          <riseup-button class="mortgage-btn margin-top-l"
                        :class="{'mortgage-without-data-advisory-chat': hasMortgageData}" title="לצרף את הקובץ" color="green"
                        :loading="uploadingFile"
                        :variant="'secondary'"
                        :icon-props="iconProps"
                        @click="onUploadButtonClick" />
          <div v-if="isErrorMessage" class="ri-small-body red-text center-text margin-top-xs">{{errorMessage}}</div>
          <separator class="margin-vertical-l or-text" text="או"/>
          <div class="ri-body black-text center-text">
            חברו את חשבון המשכנתא שלכם לרייזאפ
          </div>
          <riseup-button class="mortgage-btn margin-top-s"
                        :class="{'mortgage-without-data-advisory-chat': hasMortgageData}" title="לחבר את החשבון" color="green"
                        :variant="'secondary'"
                        :icon-props="accountIconProps"
                        @click="redirectToCredentials" />
        </div>
      </card>
      <card v-else class="cards-container padding-vertical-ml">
        <div class="flex-column">
          <div class="ri-medium-headline black-text center-text">
            קיבלנו את פרטי המשכנתא שלך -
            <span class="green-text">אנחנו על זה.</span>
          </div>
          <lottie-player
            src="/animations/mortgage-loader.json"
            autoplay
            loop
            class="mortgage-loader-animation margin-top-l"
          />
          <div class="ri-small-body black-text center-text margin-top-m">אנחנו בודקים מה המסלולים הכי מוצלחים בשבילך. זה עשוי לקחת כמה דקות.</div>
        </div>
      </card>
      </template>
    </image-header-page>
</template>

<script>
import BaseUI from '@riseupil/base-ui';
import { mapActions, mapGetters } from 'vuex';
import Segment from '@/Segment';
import router from '@/router';
import MortgageUploadFileMixin from '@/mixins/MortgageUploadFileMixin.vue';
import MortgageSummaryHelp from './MortgageSummaryHelp.vue';

export default {
  name: 'NoMortgageDataUploadPage',
  mixins: [MortgageUploadFileMixin],
  components: {
    Card: BaseUI.Card,
    RiseupButton: BaseUI.RiseupButton,
    ImageHeaderPage: BaseUI.Pages.ImageHeaderPage,
    Separator: BaseUI.Separator,
    LottiePlayer: BaseUI.LottiePlayer,
  },
  data() {
    return {
      uploadingFile: false,
      isErrorMessage: false,
      errorMessage: '',
    };
  },
  created() {
    Segment.trackUserInteraction('NoMortgageDataUploadPage_Entered');
    this.setAppHeaderProps({
      backgroundColorVariant: BaseUI.LayoutConsts.COLOR_VARIANT.GREEN,
      textColor: BaseUI.Colors.riseupLightGreen,
      logoColor: BaseUI.Colors.riseupLightGreen,
    });
  },
  computed: {
    ...mapGetters('session', ['householdName']),
    ...mapGetters('mortgages', ['hasMortgageData']),
    ...mapGetters('otsState', ['isInOtsFlow']),
    pageName() {
      return 'NoMortgageDataUploadPage';
    },
    imageHeaderProps() {
      return {
        backgroundColor: BaseUI.LayoutConsts.COLOR_VARIANT.GREEN,
        bottomSeparator: false,
        textColor: BaseUI.Colors.riseupDarkGreen,
        backgroundPosition: BaseUI.LayoutConsts.BACKGROUND_POSITIONS.BOTTOM_LEFT,
        imgHeight: 360,
      };
    },
    iconProps() {
      return {
        iconName: 'upload',
        fill: true,
        weight: 'bold',
        size: '24px',
        color: BaseUI.Colors.riseupGreen,
      };
    },
    accountIconProps() {
      return {
        iconName: 'account_balance',
        fill: true,
        weight: 'bold',
        size: '24px',
        color: BaseUI.Colors.riseupGreen,
      };
    },
  },
  methods: {
    ...mapActions('appHeader', ['setAppHeaderProps']),
    ...mapActions('modalRootStore', ['openModal', 'closeModal']),
    onUploadButtonClick() {
      this.$refs.fileInput.click();
    },
    redirectToCredentials() {
      if (this.isInOtsFlow) {
        router.push({ path: '/ots/connect' });
      } else {
        router.push({ path: '/credentials' });
      }
    },
    openPopUp() {
      this.openModal({
        component: MortgageSummaryHelp,
        props: {
          closeAction: this.closePopUp,
        },
      });
    },
    closePopUp() {
      this.closeModal();
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@riseupil/base-ui/src/scss/riseup-colors";
@import "~@riseupil/base-ui/src/scss/spacings";

  .image-header-page {
    background: $riseup_beige;

    .header-text {
      text-align: right;
    }

    .cards-container {
      background-color: $riseup_white;
    }

    .yellow-text {
      color: $riseup_yellow;
    }

    .green-text {
      color: $riseup_green;
      font-weight: 600;
    }

    .red-text {
      color: red;
    }

    .mortgage-btn {
      ::v-deep .button-titles {
        white-space: nowrap;
      }
    }

    .center-text {
      text-align: center;
    }

    .or-text {
      color: $riseup_black;
      font-size: 14px;
    }

    .url-blue {
      color: $riseup_blue;
      text-decoration: underline;
    }

    .mortgage-loader-animation {
      width: 200px;
      height: 200px;
      margin: 0 auto;
      transform: scale(1.2);
    }
  }

</style>
